import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';

// ── Minimal, safe markdown renderer (no dangerouslySetInnerHTML) ──
// Shared by the AI chat panes (CarModal/Chat.js, ServiceOrderModal/ChatTab.js).
export function renderInline(text, keyPrefix) {
  const nodes = [];
  const regex = /\[([^\]]+)\]\((https?:\/\/[^\s)]+)\)|\*\*([^*]+)\*\*|`([^`]+)`|\*([^*\n]+)\*/g;
  let lastIndex = 0;
  let match;
  let i = 0;
  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) nodes.push(text.slice(lastIndex, match.index));
    if (match[1] !== undefined) {
      nodes.push(<Link key={`${keyPrefix}-${i}`} href={match[2]} target="_blank" rel="noopener noreferrer">{match[1]}</Link>);
    } else if (match[3] !== undefined) {
      nodes.push(<strong key={`${keyPrefix}-${i}`}>{match[3]}</strong>);
    } else if (match[4] !== undefined) {
      nodes.push(<Box key={`${keyPrefix}-${i}`} component="code" sx={{ px: 0.5, py: 0.1, bgcolor: 'grey.200', borderRadius: 0.5, fontFamily: 'monospace', fontSize: '0.85em' }}>{match[4]}</Box>);
    } else if (match[5] !== undefined) {
      nodes.push(<em key={`${keyPrefix}-${i}`}>{match[5]}</em>);
    }
    lastIndex = regex.lastIndex;
    i++;
  }
  if (lastIndex < text.length) nodes.push(text.slice(lastIndex));
  return nodes;
}

export default function MarkdownText({ text }) {
  const lines = (text || '').split('\n');
  const blocks = [];
  let list = null; // { ordered, items: [] }
  let code = null; // string[]
  let inFence = false;

  const flushList = () => {
    if (list) {
      const Tag = list.ordered ? 'ol' : 'ul';
      blocks.push(
        <Box key={`l-${blocks.length}`} component={Tag} sx={{ pl: 3, my: 0.5 }}>
          {list.items.map((it, idx) => (
            <li key={idx}><Typography variant="body2" component="span" sx={{ lineHeight: 1.6 }}>{renderInline(it, `li-${blocks.length}-${idx}`)}</Typography></li>
          ))}
        </Box>
      );
      list = null;
    }
  };
  const flushCode = () => {
    if (code) {
      blocks.push(
        <Box key={`c-${blocks.length}`} component="pre" sx={{ p: 1.25, my: 0.5, bgcolor: 'grey.900', color: 'grey.100', borderRadius: 1, overflow: 'auto', fontSize: '0.8rem', fontFamily: 'monospace' }}>
          {code.join('\n')}
        </Box>
      );
      code = null;
    }
  };

  lines.forEach((raw) => {
    if (raw.trim().startsWith('```')) {
      if (inFence) { flushCode(); inFence = false; }
      else { flushList(); code = []; inFence = true; }
      return;
    }
    if (inFence) { code.push(raw); return; }

    const bullet = raw.match(/^\s*[-*]\s+(.*)$/);
    const numbered = raw.match(/^\s*\d+\.\s+(.*)$/);
    const heading = raw.match(/^(#{1,4})\s+(.*)$/);

    if (bullet) {
      if (!list || list.ordered) { flushList(); list = { ordered: false, items: [] }; }
      list.items.push(bullet[1]);
    } else if (numbered) {
      if (!list || !list.ordered) { flushList(); list = { ordered: true, items: [] }; }
      list.items.push(numbered[1]);
    } else if (heading) {
      flushList();
      const level = heading[1].length;
      blocks.push(
        <Typography key={`h-${blocks.length}`} variant={level <= 1 ? 'subtitle1' : 'subtitle2'} sx={{ fontWeight: 700, mt: 1, mb: 0.25 }}>
          {renderInline(heading[2], `h-${blocks.length}`)}
        </Typography>
      );
    } else if (raw.trim() === '') {
      flushList();
    } else {
      flushList();
      blocks.push(
        <Typography key={`p-${blocks.length}`} variant="body2" sx={{ lineHeight: 1.6, my: 0.4, wordBreak: 'break-word' }}>
          {renderInline(raw, `p-${blocks.length}`)}
        </Typography>
      );
    }
  });
  flushList();
  flushCode();
  return <>{blocks}</>;
}
